// Iter268 · L1 — Gestor LLM (Admin → Pando & IA). Config global de
// providers[] + routes[] por módulo. Pando/SofIA (LiteLLM propio,
// OpenAI-compat) es el proveedor primario. Sin ingress publicado hoy →
// base_url vacío = "No configurado" en toda la UI, nunca un error crudo.
// Corrección 2026-08-31: SIN fallback automático a otros proveedores.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Settings, Wifi, WifiOff, Loader2, RefreshCw, Save, KeyRound, Link2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";

const MODULE_LABELS = {
  licitaciones_analysis: "Análisis de licitaciones",
  licitaciones_chat:     "Chat de licitaciones",
  mercado_classify:      "Mercado · Clasificación",
  mercado_enrich:        "Mercado · Enriquecimiento (noticias)",
  offers_wizard:         "Ofertas TIC · Wizard IA",
  checklists:            "Checklists IA",
  committee:             "Comité",
  tender360_m0:          "Tender360 M0 · Scorer",
  tender360_bidai:       "Tender360 · BidAI Knowledge",
  openai_compat_default: "Gateway OpenAI-compat (default)",
};

export default function LlmSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [baseUrlDraft, setBaseUrlDraft] = useState("");
  const [health, setHealth] = useState(null);
  const [checkingHealth, setCheckingHealth] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/llm-settings`);
      setSettings(r.data);
      const pando = (r.data.providers || []).find(p => p.id === "pando");
      setBaseUrlDraft(pando?.base_url || "");
    } catch (e) {
      toast.error("No se pudo cargar el Gestor LLM");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pando = (settings?.providers || []).find(p => p.id === "pando");
  const otherProviders = (settings?.providers || []).filter(p => p.id !== "pando");

  const saveBaseUrl = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const providers = settings.providers.map(p =>
        p.id === "pando" ? { ...p, base_url: baseUrlDraft.trim() } : p
      );
      const r = await axios.put(`${API}/admin/llm-settings`, { providers, routes: settings.routes });
      setSettings(r.data);
      setHealth(null);
      toast.success("URL de Pando guardada");
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const checkHealth = async () => {
    setCheckingHealth(true);
    setHealth(null);
    try {
      const r = await axios.get(`${API}/admin/llm-settings/health?provider_id=pando`);
      setHealth(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setHealth(detail?.error === "pando_not_configured"
        ? { ok: false, error: "pando_not_configured" }
        : { ok: false, error: "error" });
    } finally {
      setCheckingHealth(false);
    }
  };

  const updateRoute = (module, field, value) => {
    setSettings(s => ({
      ...s,
      routes: s.routes.map(r => r.module === module ? { ...r, [field]: value } : r),
    }));
  };

  const saveRoutes = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/llm-settings`, { providers: settings.providers, routes: settings.routes });
      setSettings(r.data);
      toast.success("Rutas guardadas");
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || "Error al guardar rutas");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-2 text-sm text-gray-500" data-testid="llm-settings-loading">
        <Loader2 size={14} className="animate-spin"/> Cargando Gestor LLM…
      </div>
    );
  }
  if (!settings) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mb-4" data-testid="llm-settings-panel">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Settings size={16} className="text-indigo-700"/>
          <h3 className="font-bold text-gray-900 font-['Outfit']">GESTOR LLM · Pando (SofIA)</h3>
        </div>
        <button onClick={load} disabled={loading}
          className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
          data-testid="llm-settings-refresh">
          <RefreshCw size={12}/> Refrescar
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Pando/SofIA (LiteLLM propio) es el proveedor primario del gateway <code>/api/openai-compat/v1</code>.
        <strong> Sin fallback automático</strong> a Emergent/Anthropic/Perplexity/OpenAI/Gemini — si Pando no
        está configurado o falla, el caller recibe un error limpio. Hoy no hay ingress interno publicado por
        OpenSistemas: mientras tanto, el resto de módulos sigue usando su proveedor actual sin cambios.
      </p>

      {/* Pando provider card */}
      <div className="border border-indigo-100 bg-indigo-50/40 rounded-lg p-4 mb-4" data-testid="pando-provider-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-indigo-800 uppercase">Pando (kind: pando)</span>
            {health === null && !pando?.base_url && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 inline-flex items-center gap-1" data-testid="pando-status-not-configured">
                <WifiOff size={11}/> No configurado
              </span>
            )}
            {health?.ok && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid="pando-status-ok">
                <Wifi size={11}/> Responde ({health.models_count} modelos)
              </span>
            )}
            {health && !health.ok && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 inline-flex items-center gap-1" data-testid="pando-status-error">
                <AlertTriangle size={11}/> {health.error === "pando_not_configured" ? "No configurado" : health.error}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[11px] text-gray-500">
            <KeyRound size={12}/>
            {pando?.key_configured
              ? <span data-testid="pando-key-status">Key configurada (••••{pando.key_last4})</span>
              : <span data-testid="pando-key-status">Key NO configurada (env <code>PANDO_LITELLM_KEY</code>)</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 flex-1 min-w-[260px]">
            <Link2 size={13} className="text-gray-400"/>
            <input
              type="text"
              value={baseUrlDraft}
              onChange={(e) => setBaseUrlDraft(e.target.value)}
              placeholder="https://litellm-pando.internal.valorizasm.com (aún sin ingress publicado)"
              className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-2"
              data-testid="pando-base-url-input"
            />
          </div>
          <button onClick={saveBaseUrl} disabled={saving}
            className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="pando-save-base-url">
            {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Guardar URL
          </button>
          <button onClick={checkHealth} disabled={checkingHealth}
            className="text-xs border border-indigo-200 text-indigo-700 hover:bg-indigo-50 px-3 py-2 rounded-lg inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="pando-health-check">
            {checkingHealth ? <Loader2 size={12} className="animate-spin"/> : <Wifi size={12}/>} Probar conexión
          </button>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          Modelos SofIA: {(pando?.models || []).join(" · ")}
        </p>
      </div>

      {/* Otros providers (solo lectura — gestionados por env, sin UI de key aquí) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        {otherProviders.map(p => (
          <div key={p.id} className="border border-gray-100 rounded-lg p-3 text-xs" data-testid={`provider-${p.id}`}>
            <p className="font-bold text-gray-700 uppercase">{p.kind}</p>
            <p className="text-gray-500">Gestionado por variables de entorno de la plataforma (fallback deshabilitado desde este gateway).</p>
          </div>
        ))}
      </div>

      {/* Routes table */}
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold text-gray-800">Rutas por módulo</h4>
          <button onClick={saveRoutes} disabled={saving}
            className="text-xs bg-gray-900 hover:bg-black text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="llm-routes-save">
            {saving ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>} Guardar rutas
          </button>
        </div>
        <p className="text-[11px] text-gray-500 mb-3">
          Config declarativa para cuando Pando esté vivo — hoy estos módulos siguen usando su proveedor actual
          (Emergent/Anthropic) sin cambio de comportamiento real.
        </p>
        <div className="space-y-1.5" data-testid="llm-routes-list">
          {(settings.routes || []).map((r) => {
            const providerModels = (settings.providers.find(p => p.id === r.provider_id)?.models) || [];
            return (
              <div key={r.module} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center border border-gray-50 hover:border-gray-200 rounded-lg p-2" data-testid={`route-row-${r.module}`}>
                <span className="text-xs font-medium text-gray-700">{MODULE_LABELS[r.module] || r.module}</span>
                <select
                  value={r.provider_id}
                  onChange={(e) => updateRoute(r.module, "provider_id", e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5"
                  data-testid={`route-provider-${r.module}`}
                >
                  {settings.providers.map(p => <option key={p.id} value={p.id}>{p.kind}</option>)}
                </select>
                <select
                  value={r.model}
                  onChange={(e) => updateRoute(r.module, "model", e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5"
                  data-testid={`route-model-${r.module}`}
                >
                  {providerModels.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
